import { Injectable, Injector } from '@angular/core';
import { TranslateScopes } from '@cloud-twin-v2/cloud-twin-shared/core/translate-scopes';
import { InstancesRegisterService } from '@cloud-twin-v2/generated/services/instances-register.service';
import {
  InstanceResponseDto,
  SnapshotResponseDto,
} from '@cloud-twin-v2/generated/simulation.generated.client';
import { BaseService } from '@common-modules/shared/base.service';
import { globalUtilsHelper } from '@common-modules/shared/helpers/global-utils-helper';
import { LocalizationHelperService } from '@common-modules/shared/localization/localization-helper.service';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  MasterOperatorConnectItemsSettings,
  MasterOperatorConnectSettings,
} from '../models/master-operator-connect-settings';
import { BaseInstanceResponseDto } from '../models/master-operator-instance';
import { MasterOperatorType } from '../models/master-operator-type';

@Injectable()
export class MasterOperatorService extends BaseService {
  constructor(
    injector: Injector,
    private _localization: LocalizationHelperService,
    private _instancesRegisterService: InstancesRegisterService
  ) {
    super(injector);
  }

  getInstances({ type }: { type: MasterOperatorType }): Observable<InstanceResponseDto[]> {
    return this._instancesRegisterService
      .getInstances()
      .pipe(
        map((instances) => instances.filter((instance) => this.isOfType(instance as any, type)))
      );
  }

  getAllInstances(): Observable<InstanceResponseDto[]> {
    return this._instancesRegisterService.getInstances();
  }

  isOfType(instance: BaseInstanceResponseDto, type: MasterOperatorType): boolean {
    if (type === MasterOperatorType.Master) {
      return !instance.realTimeId; // If it does not has realtimeId, then it is a realtime.
    }
    return !!instance.realTimeId;
  }

  instanceReadableLabel(instance: BaseInstanceResponseDto): string {
    let label;
    if (this.isOfType(instance, MasterOperatorType.Master)) {
      label = instance.modelId;
    } else {
      // By now, only operators have name.
      label = instance.name;
    }

    label = globalUtilsHelper.removeFilePath(label);

    return label;
  }

  snapshotReadableLabel(snapshot: SnapshotResponseDto): string {
    return snapshot.name;
  }

  getSingleId(connectSettings: MasterOperatorConnectSettings): string {
    // TODO: JUST GUESSING HOW THIS FIELD IS BUILT.
    return connectSettings.instanceId;
  }

  getTitle$(connectSettings: MasterOperatorConnectItemsSettings): Observable<string> {
    const baseKey = 'master-operator.titles';

    const firstInstance = connectSettings.items[0];
    let instanceName = this.instanceReadableLabel(firstInstance);

    let result$;
    if (connectSettings.type === MasterOperatorType.Master) {
      result$ = this._localization.get(`${baseKey}.realtime`, {
        instanceName,
      });
    } else {
      result$ = this._localization.get(`${baseKey}.operations`, {
        instanceName,
        operatorName: firstInstance.name,
      });
    }

    return result$;
  }

  // A proposal that should be a bit more refined.
  buildConfiguredSingleTitle(
    connectSettings: MasterOperatorConnectSettings,
    type: MasterOperatorType
  ): Observable<string> {
    const entityName$ = this._localization.get(
      `${TranslateScopes.MasterOperator}.entities.${
        type === MasterOperatorType.Master ? 'master' : 'operator'
      }`
    );

    const { instanceId, snapshotId, scenarioId } = connectSettings;
    return entityName$.pipe(
      switchMap((entityName) => {
        return this._localization.get(`${TranslateScopes.MasterOperator}.titles.configured-title`, {
          entityName,
          instanceId,
          snapshotId,
          scenarioId,
        });
      })
    );
  }
}
